import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '../../components/ui/SEO';
import { CircleAlert, ShoppingBag } from 'lucide-react';

interface Product {
  id: number; code: string; name: string; category: string;
  price: number; description: string | null; detail_content: string | null;
  image_url: string | null; gallery: string[];
  features: string[]; specs: Record<string, string>;
  stock: number; sold_out: number;
}

export default function ProductDetailPage() {
  const { id }     = useParams<{ id: string }>();
  const navigate   = useNavigate();
  const [product,  setProduct]  = useState<Product | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mainImg,  setMainImg]  = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    fetch(`/api/catalog/${id}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then((data: Product) => {
        setProduct(data);
        setMainImg(data.image_url);
        setLoading(false);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-stone-600 animate-pulse">제품 정보를 불러오는 중...</p>
    </div>
  );

  if (notFound || !product) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <CircleAlert size={52} className="text-stone-400" aria-hidden="true" />
      <p className="text-stone-500">제품을 찾을 수 없습니다.</p>
      <button onClick={() => navigate('/catalog')}
        className="bg-amber-600 text-white font-bold px-6 py-2 rounded-full">
        카탈로그로 돌아가기
      </button>
    </div>
  );

  const isSoldOut = product.sold_out === 1;
  const allImages = [product.image_url, ...(product.gallery ?? [])].filter(Boolean) as string[];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={product.name}
        path={`/product/${product.id}`}
        description={product.description ?? `${product.name} — COBRAND 전문 제작 ${product.category}. ${isSoldOut ? '현재 품절' : `재고 ${product.stock}개`}.`}
        keywords={`${product.name},${product.category},COBRAND,가방`}
        image={product.image_url ?? undefined}
        type="product"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.name,
          "description": product.description,
          "image": product.image_url,
          "brand": { "@type": "Brand", "name": "COBRAND" },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "KRW",
            "price": product.price,
            "availability": isSoldOut
              ? "https://schema.org/OutOfStock"
              : "https://schema.org/InStock",
            "url": `https://brand.bagself.com/product/${product.id}`
          }
        }}
      />

      {/* 브레드크럼 */}
      <div className="bg-stone-50 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm text-stone-500">
          <Link to="/" className="hover:text-amber-600 transition">홈</Link>
          <span>/</span>
          <Link to="/catalog" className="hover:text-amber-600 transition">카탈로그</Link>
          <span>/</span>
          <span className="text-stone-800 font-medium truncate">{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* ── 이미지 영역 ── */}
          <div className="space-y-4">
            {/* 메인 이미지 */}
            <div className="relative bg-stone-100 rounded-2xl overflow-hidden aspect-square flex items-center justify-center">
              {mainImg ? (
                <img src={mainImg} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <ShoppingBag size={64} className="text-stone-300" aria-label="제품 이미지 없음" />
              )}
              {isSoldOut && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white text-2xl font-bold tracking-[0.3em]">SOLD OUT</span>
                </div>
              )}
            </div>

            {/* 갤러리 썸네일 */}
            {allImages.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-1">
                {allImages.map((img, i) => (
                  <button key={i} onClick={() => setMainImg(img)}
                    className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition ${
                      mainImg === img ? 'border-amber-500' : 'border-transparent hover:border-stone-300'
                    }`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── 제품 정보 ── */}
          <div className="flex flex-col">

            {/* 카테고리 배지 */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold bg-stone-100 text-stone-600 px-3 py-1 rounded-full">
                {product.category}
              </span>
              {product.category === '내부판매용' && (
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                  isSoldOut ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                }`}>
                  {isSoldOut ? '품절' : `재고 ${product.stock}개`}
                </span>
              )}
            </div>

            {/* 제품명 */}
            <h1 className="text-3xl font-bold text-stone-900 mb-2">{product.name}</h1>
            <p className="text-sm text-stone-600 font-mono mb-4">{product.code}</p>

            {/* 가격 */}
            <div className="flex items-baseline gap-3 mb-6">
              <span className={`text-3xl font-bold ${isSoldOut ? 'text-stone-600' : 'text-amber-600'}`}>
                {product.price.toLocaleString()}원
              </span>
              {isSoldOut && (
                <span className="text-sm text-red-500 font-semibold">현재 품절</span>
              )}
            </div>

            {/* 짧은 설명 */}
            {product.description && (
              <p className="text-stone-600 leading-relaxed mb-6 text-sm border-l-4 border-amber-400 pl-4">
                {product.description}
              </p>
            )}

            {/* 특징 목록 */}
            {product.features?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-bold text-stone-700 mb-3">제품 특징</h3>
                <ul className="space-y-2">
                  {product.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                      <span className="text-amber-500 mt-0.5 flex-shrink-0">✦</span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 스펙 테이블 */}
            {product.specs && Object.keys(product.specs).length > 0 && (
              <div className="mb-8 bg-stone-50 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    {Object.entries(product.specs).map(([k, v]) => (
                      <tr key={k} className="border-b border-stone-100 last:border-0">
                        <td className="px-4 py-3 font-semibold text-stone-500 w-1/3">{k}</td>
                        <td className="px-4 py-3 text-stone-700">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3 mt-auto">
              {isSoldOut ? (
                <div className="flex-1 bg-stone-100 text-stone-600 font-bold py-4 rounded-2xl text-center text-sm">
                  현재 품절된 제품입니다
                </div>
              ) : (
                <Link to="/oem"
                  className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold py-4 rounded-2xl text-center text-sm transition">
                  구매 / 제작 문의
                </Link>
              )}
              <Link to="/catalog"
                className="flex-1 border-2 border-stone-200 hover:border-stone-400 text-stone-600 font-semibold py-4 rounded-2xl text-center text-sm transition">
                카탈로그 목록
              </Link>
            </div>
          </div>
        </div>

        {/* ── 상세 설명 (HTML) ── */}
        {product.detail_content && (
          <div className="mt-16 pt-12 border-t border-stone-100">
            <h2 className="text-2xl font-bold text-stone-900 mb-8 text-center">제품 상세 정보</h2>
            <div
              className="prose max-w-none text-stone-700 leading-relaxed"
              style={{
                lineHeight: '1.9',
                fontSize: '15px',
              }}
              dangerouslySetInnerHTML={{ __html: product.detail_content }}
            />
          </div>
        )}

        {/* ── 연관 문의 ── */}
        <div className="mt-20 bg-white text-stone-900 rounded-2xl p-10 text-center">
          <h2 className="text-xl font-bold mb-2">이 제품이 마음에 드셨나요?</h2>
          <p className="text-stone-600 text-sm mb-6">OEM/ODM 전문 제작으로 원하는 수량, 색상, 디자인으로 맞춤 제작이 가능합니다.</p>
          <Link to="/oem"
            className="inline-block bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 py-3 rounded-full transition">
            전문 제작 문의
          </Link>
        </div>
      </div>
    </div>
  );
}
